import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut, Save, Plus, Trash2, Loader2, UserPlus, ArrowLeft, Check, Send, Globe, ExternalLink, Download, ShieldCheck, Clock, Inbox, MousePointerClick, TrendingUp } from "lucide-react";
import AuthLogin from "@/components/AuthLogin";
import ImageUpload from "@/components/ImageUpload";
import { authApi, getToken, clearToken, api, imgUrl } from "@/lib/api";

const FIRM_BENEFITS = [
  "Dostávajte overené dopyty na zateplenie priamo z vášho okresu",
  "Vlastná verejná vizitka s logom, referenciami a odkazmi na sociálne siete",
  "Kontakt zákazníka vám ukážeme, keď kliknete „Mám záujem“",
  "Max. 2 e-maily týždenne — žiadny spam, jednoduché odhlásenie",
];

function Benefits() {
  return (
    <div className="mt-6 border-t border-white/10 pt-6" data-testid="firm-benefits">
      <p className="font-mono2 text-gold uppercase tracking-[0.25em] text-[11px] mb-3">Prečo sa registrovať</p>
      <ul className="space-y-2">
        {FIRM_BENEFITS.map((b, i) => (
          <li key={i} className="flex items-start gap-2 text-white/70 text-sm">
            <Check className="w-4 h-4 text-gold shrink-0 mt-0.5" /> {b}
          </li>
        ))}
      </ul>
    </div>
  );
}

function RegisterForm({ onBack }) {
  const [f, setF] = useState({ company: "", email: "", password: "", phone: "", country: "SK", district: "", website: "" });
  const [districts, setDistricts] = useState({ SK: [], CZ: [] });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/firm/districts").then((r) => setDistricts(r.data)).catch(() => {}); }, []);
  const opts = districts[f.country] || [];
  const submit = async () => {
    if (!f.company || !f.email || !f.password || !f.district) return toast.error("Vyplňte firmu, e-mail, heslo a okres.");
    setBusy(true);
    try {
      await api.post("/firm/register", f);
      toast.success("Registrácia úspešná! Teraz sa prihláste.");
      onBack();
    } catch (e) { toast.error(e?.response?.data?.detail || "Registrácia zlyhala"); } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-ink px-5 py-16">
      <div className="w-full max-w-md border border-white/10 bg-ink-2 p-8" data-testid="firm-register">
        <div className="w-12 h-12 bg-gold flex items-center justify-center mb-6"><UserPlus className="w-6 h-6 text-ink" /></div>
        <h1 className="font-display text-4xl text-white mb-1">Registrácia firmy</h1>
        <p className="text-white/50 text-sm mb-6">Realizujete zateplenie a fasády? Vytvorte si účet a dostávajte dopyty.</p>
        <div className="space-y-3">
          <input placeholder="Názov firmy *" data-testid="freg-company" value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} className="w-full bg-transparent border border-white/20 focus:border-gold outline-none text-white px-4 py-3" />
          <input placeholder="E-mail *" data-testid="freg-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="w-full bg-transparent border border-white/20 focus:border-gold outline-none text-white px-4 py-3" />
          <input placeholder="Telefón" data-testid="freg-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="w-full bg-transparent border border-white/20 focus:border-gold outline-none text-white px-4 py-3" />
          <div className="grid grid-cols-2 gap-3">
            <select value={f.country} data-testid="freg-country" onChange={(e) => setF({ ...f, country: e.target.value, district: "" })} className="w-full bg-ink border border-white/20 focus:border-gold outline-none text-white px-3 py-3">
              <option value="SK">Slovensko</option><option value="CZ">Česko</option>
            </select>
            <select value={f.district} data-testid="freg-district" onChange={(e) => setF({ ...f, district: e.target.value })} className="w-full bg-ink border border-white/20 focus:border-gold outline-none text-white px-3 py-3">
              <option value="">Okres *</option>
              {opts.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <input placeholder="Web (nepovinné)" data-testid="freg-website" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} className="w-full bg-transparent border border-white/20 focus:border-gold outline-none text-white px-4 py-3" />
          <input type="password" placeholder="Heslo *" data-testid="freg-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="w-full bg-transparent border border-white/20 focus:border-gold outline-none text-white px-4 py-3" />
        </div>
        <button onClick={submit} disabled={busy} data-testid="freg-submit" className="w-full mt-4 bg-gold text-ink py-3 font-bold uppercase tracking-wider hover:bg-gold-light flex items-center justify-center gap-2">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Zaregistrovať sa"}
        </button>
        <button onClick={onBack} className="w-full text-white/40 text-xs mt-3 hover:text-white flex items-center justify-center gap-1"><ArrowLeft className="w-3 h-3" /> Už mám účet — prihlásiť</button>
        <Benefits />
      </div>
    </div>
  );
}

function WebImport({ onAdd }) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [imgs, setImgs] = useState([]);
  const [sel, setSel] = useState({});
  const run = async () => {
    if (!url) return toast.error("Zadajte URL svojej stránky.");
    setBusy(true); setImgs([]); setSel({});
    try {
      const { data } = await authApi().post("/firm/import-references", { url });
      if (!data.images.length) toast.info("Na stránke sme nenašli vhodné fotky.");
      setImgs(data.images);
    } catch (e) { toast.error(e?.response?.data?.detail || "Načítanie zlyhalo"); } finally { setBusy(false); }
  };
  const addSelected = () => {
    const chosen = imgs.filter((_, i) => sel[i]);
    if (!chosen.length) return toast.error("Označte aspoň jednu fotku.");
    onAdd(chosen.map((image) => ({ image, image_after: "", description: "" })));
    toast.success(`Pridané: ${chosen.length} fotiek`);
    setImgs([]); setSel({});
  };
  return (
    <div className="border border-white/10 p-5" data-testid="firm-webimport">
      <div className="flex items-center gap-2 mb-1"><Globe className="w-5 h-5 text-gold" /><h3 className="font-display text-xl">Načítať referencie z webu</h3></div>
      <p className="text-white/40 text-xs mb-3">Zadajte adresu vašej stránky — pokúsime sa nájsť fotky realizácií. Je to pomôcka, výsledok si potvrdíte.</p>
      <div className="flex gap-2">
        <input value={url} onChange={(e) => setUrl(e.target.value)} data-testid="firm-webimport-url" placeholder="napr. www.mojafirma.sk" className="flex-1 bg-transparent border border-white/15 px-3 py-2 text-sm focus:border-gold outline-none" />
        <button onClick={run} disabled={busy} data-testid="firm-webimport-run" className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 text-sm font-bold uppercase tracking-wider flex items-center gap-2">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Načítať
        </button>
      </div>
      {imgs.length > 0 && (
        <div className="mt-4">
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {imgs.map((src, i) => (
              <button key={i} onClick={() => setSel({ ...sel, [i]: !sel[i] })} data-testid={`firm-webimport-img-${i}`}
                className={`relative aspect-[4/3] overflow-hidden border-2 ${sel[i] ? "border-gold" : "border-transparent"}`}>
                <img src={src} alt="" className="w-full h-full object-cover" />
                {sel[i] && <span className="absolute top-1 right-1 bg-gold text-ink rounded-full p-0.5"><Check className="w-3 h-3" /></span>}
              </button>
            ))}
          </div>
          <button onClick={addSelected} data-testid="firm-webimport-add" className="mt-3 bg-gold text-ink px-4 py-2 text-sm font-bold uppercase tracking-wider hover:bg-gold-light flex items-center gap-2"><Plus className="w-4 h-4" /> Pridať označené</button>
        </div>
      )}
    </div>
  );
}

export default function FirmPortal() {
  const [authed, setAuthed] = useState(false);
  const [f, setF] = useState(null);
  const [stats, setStats] = useState(null);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState("login");

  const load = async () => {
    try {
      const meRes = await api.get("/auth/me", { headers: { Authorization: `Bearer ${getToken()}` } });
      if (meRes.data.role !== "firm") { clearToken(); setAuthed(false); toast.error("Tento účet nie je firemný. Prihláste sa firemným účtom."); return; }
      const { data } = await authApi().get("/firm/me");
      setF({
        company: data.company || "", phone: data.phone || "", website: data.website || "", city: data.city || "",
        bio: data.bio || "", logo: data.logo || "", facebook: data.facebook || "", instagram: data.instagram || "",
        tiktok: data.tiktok || "", x: data.x || "", youtube: data.youtube || "",
        references: data.references || [], district: data.district, country: data.country,
        slug: data.slug, card_submitted: data.card_submitted, card_approved: data.card_approved,
      });
      setAuthed(true);
      authApi().get("/firm/stats").then((r) => setStats(r.data)).catch(() => {});
    } catch { clearToken(); setAuthed(false); }
  };
  useEffect(() => { if (getToken()) load(); }, []);

  const persist = async (submit) => {
    setSaving(true);
    try {
      const payload = {
        company: f.company, phone: f.phone, website: f.website, city: f.city, bio: f.bio, logo: f.logo,
        facebook: f.facebook, instagram: f.instagram, tiktok: f.tiktok, x: f.x, youtube: f.youtube,
        references: f.references, ...(submit ? { submit: true } : {}),
      };
      const { data } = await authApi().put("/firm/me", payload);
      setF((p) => ({ ...p, slug: data.slug, card_submitted: data.card_submitted, card_approved: data.card_approved }));
      toast.success(submit ? "Vizitka odoslaná na schválenie." : "Uložené");
    } catch (e) { toast.error(e?.response?.data?.detail || "Uloženie zlyhalo"); } finally { setSaving(false); }
  };

  if (!authed || !f) {
    if (mode === "register") return <RegisterForm onBack={() => setMode("login")} />;
    return (
      <div className="relative">
        <AuthLogin role="firm" title="Firemný portál" onSuccess={() => load()} footer={<Benefits />} />
        <button onClick={() => setMode("register")} data-testid="firm-show-register"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gold text-ink px-5 py-2.5 text-sm font-bold uppercase tracking-wider hover:bg-gold-light">
          Nemáte účet? Zaregistrujte sa
        </button>
      </div>
    );
  }

  const addRefs = (arr) => setF({ ...f, references: [...f.references, ...arr] });
  const addRef = () => addRefs([{ image: "", image_after: "", description: "" }]);
  const updRef = (i, k, v) => setF({ ...f, references: f.references.map((r, idx) => idx === i ? { ...r, [k]: v } : r) });
  const delRef = (i) => setF({ ...f, references: f.references.filter((_, idx) => idx !== i) });

  const socials = [
    ["facebook", "Facebook"], ["instagram", "Instagram"], ["tiktok", "TikTok"], ["x", "X (Twitter)"], ["youtube", "YouTube"],
  ];

  return (
    <div className="min-h-screen bg-ink text-white" data-testid="firm-portal">
      <header className="border-b border-white/10 px-5 md:px-10 h-20 flex items-center justify-between sticky top-0 bg-ink/90 backdrop-blur z-10">
        <div className="font-display text-3xl">ZATEPLENIA<span className="text-gold">.EU</span> <span className="text-white/40 text-lg">/ firma</span></div>
        <button onClick={() => { clearToken(); setAuthed(false); }} className="flex items-center gap-2 px-4 py-2 border border-white/20 hover:border-white/50 text-sm"><LogOut className="w-4 h-4" /> Odhlásiť</button>
      </header>

      <div className="max-w-[900px] mx-auto px-5 md:px-10 py-10 space-y-8">
        <div>
          <h1 className="font-display text-4xl">{f.company}</h1>
          <p className="text-white/50 text-sm mt-1">Okres: {f.district} · {f.country}</p>
        </div>

        {/* Stav vizitky */}
        <div data-testid="firm-card-status" className={`border p-4 flex items-center gap-3 ${f.card_approved ? "border-green-400/40 bg-green-400/5" : f.card_submitted ? "border-amber-400/40 bg-amber-400/5" : "border-white/10 bg-ink-2"}`}>
          {f.card_approved ? <ShieldCheck className="w-5 h-5 text-green-400 shrink-0" /> : <Clock className="w-5 h-5 text-amber-400 shrink-0" />}
          <div className="flex-1 text-sm">
            {f.card_approved ? (
              <>Vaša vizitka je <b className="text-green-400">zverejnená</b>. <a href={`/firma/${f.slug}`} target="_blank" rel="noreferrer" className="text-gold inline-flex items-center gap-1 hover:underline">Zobraziť <ExternalLink className="w-3 h-3" /></a></>
            ) : f.card_submitted ? (
              <>Vizitka <b className="text-amber-400">čaká na schválenie</b> administrátorom. Po schválení bude verejná.</>
            ) : (
              <>Vyplňte vizitku a kliknite na <b>„Odoslať na schválenie“</b>, aby sme ju mohli zverejniť.</>
            )}
          </div>
        </div>

        {/* Prehľad dopytov */}
        <section data-testid="firm-stats-overview">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              ["Dopyty prišli", stats?.leads ?? 0, Inbox, "text-gold"],
              ["Mám záujem – klikov", stats?.interested ?? 0, MousePointerClick, "text-green-400"],
              ["Upozornení e-mailom", stats?.notifications ?? 0, Send, "text-white"],
              ["Vo fronte (súhrn)", stats?.queued ?? 0, Clock, "text-amber-400"],
            ].map(([label, val, Icon, color]) => (
              <div key={label} className="bg-ink-2 border border-white/10 p-4" data-testid={`firm-stat-${label}`}>
                <Icon className={`w-4 h-4 ${color} mb-2`} />
                <div className={`text-3xl font-display ${color}`}>{val}</div>
                <div className="text-white/40 text-[11px] font-mono2 uppercase tracking-widest mt-1">{label}</div>
              </div>
            ))}
          </div>
          {stats?.recent?.length > 0 && (
            <div className="mt-4 border border-white/10" data-testid="firm-recent-leads">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10 text-white/60 text-xs font-mono2 uppercase tracking-widest">
                <TrendingUp className="w-4 h-4 text-gold" /> Posledné dopyty vo vašom okrese
              </div>
              <div className="divide-y divide-white/5">
                {stats.recent.map((n, i) => (
                  <div key={i} className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`firm-recent-${i}`}>
                    <div className="flex-1 min-w-0">
                      {n.leads.map((l, j) => (
                        <div key={j} className="text-sm text-white/70">
                          <span className="text-gold">{l.okres}</span> · {l.material}{l.area ? ` · ${l.area} m²` : ""} · {l.term}
                          {l.total ? <span className="text-white/40"> · ~{Math.round(l.total).toLocaleString("sk-SK")} €</span> : null}
                        </div>
                      ))}
                      <div className="text-white/30 text-[11px] mt-0.5">{n.date ? new Date(n.date).toLocaleDateString("sk-SK") : ""}</div>
                    </div>
                    {n.interested ? (
                      <span className="shrink-0 text-green-400 text-xs font-bold uppercase tracking-widest flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Kontakt zobrazený</span>
                    ) : (
                      <a href={`${(process.env.REACT_APP_BACKEND_URL || "")}/api/firm/i/${n.token}`} target="_blank" rel="noreferrer"
                        data-testid={`firm-recent-interest-${i}`}
                        className="shrink-0 bg-gold text-ink px-3 py-1.5 text-xs font-bold uppercase tracking-widest hover:bg-gold-light flex items-center gap-1">
                        Mám záujem <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        <section className="border border-white/10 p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-white/50 text-xs mb-1">Názov firmy</label>
            <input value={f.company} data-testid="firm-company-input" onChange={(e) => setF({ ...f, company: e.target.value })} className="w-full bg-transparent border border-white/15 px-3 py-2 focus:border-gold outline-none" />
          </div>
          <div>
            <label className="block text-white/50 text-xs mb-1">Logo firmy</label>
            <ImageUpload value={f.logo} testid="firm-logo" label="Nahrať logo" onUploaded={(url) => setF({ ...f, logo: url })} />
          </div>
          <div>
            <label className="block text-white/50 text-xs mb-1">Telefón</label>
            <input value={f.phone} data-testid="firm-phone-input" onChange={(e) => setF({ ...f, phone: e.target.value })} className="w-full bg-transparent border border-white/15 px-3 py-2 focus:border-gold outline-none" />
          </div>
          <div>
            <label className="block text-white/50 text-xs mb-1">Mesto pôsobenia</label>
            <input value={f.city} data-testid="firm-city-input" onChange={(e) => setF({ ...f, city: e.target.value })} className="w-full bg-transparent border border-white/15 px-3 py-2 focus:border-gold outline-none" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-white/50 text-xs mb-1">Web</label>
            <input value={f.website} data-testid="firm-website-input" onChange={(e) => setF({ ...f, website: e.target.value })} placeholder="www.mojafirma.sk" className="w-full bg-transparent border border-white/15 px-3 py-2 focus:border-gold outline-none" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-white/50 text-xs mb-1">O firme</label>
            <textarea value={f.bio} data-testid="firm-bio-input" onChange={(e) => setF({ ...f, bio: e.target.value })} rows={3} placeholder="Krátko o vašej firme, skúsenostiach a špecializácii…" className="w-full bg-transparent border border-white/15 px-3 py-2 resize-none focus:border-gold outline-none" />
          </div>
        </section>

        {/* Sociálne siete */}
        <section className="border border-white/10 p-6">
          <h2 className="font-display text-2xl mb-4">Sociálne siete</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {socials.map(([key, label]) => (
              <div key={key}>
                <label className="block text-white/50 text-xs mb-1">{label}</label>
                <input value={f[key]} data-testid={`firm-social-${key}`} onChange={(e) => setF({ ...f, [key]: e.target.value })} placeholder="odkaz alebo @meno" className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm focus:border-gold outline-none" />
              </div>
            ))}
          </div>
        </section>

        {/* Import z webu */}
        <WebImport onAdd={addRefs} />

        {/* Referencie */}
        <section className="border border-white/10 p-6" data-testid="firm-references">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-2xl">Referencie (pred / po)</h2>
            <button onClick={addRef} data-testid="firm-add-reference" className="flex items-center gap-1 text-gold text-sm"><Plus className="w-4 h-4" /> Pridať referenciu</button>
          </div>
          <div className="space-y-4">
            {f.references.length === 0 && <p className="text-white/30 text-sm">Zatiaľ žiadne referencie. Pridajte fotky svojich realizácií alebo ich načítajte z webu.</p>}
            {f.references.map((r, i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start border border-white/10 p-3" data-testid={`firm-ref-${i}`}>
                <div className="md:col-span-4">
                  <label className="block text-white/40 text-[11px] mb-1">Pred</label>
                  <ImageUpload value={r.image} testid={`firm-ref-before-${i}`} label="Fotka pred" onUploaded={(url) => updRef(i, "image", url)} />
                </div>
                <div className="md:col-span-4">
                  <label className="block text-white/40 text-[11px] mb-1">Po</label>
                  <ImageUpload value={r.image_after} testid={`firm-ref-after-${i}`} label="Fotka po" onUploaded={(url) => updRef(i, "image_after", url)} />
                </div>
                <div className="md:col-span-3">
                  <label className="block text-white/40 text-[11px] mb-1">Popis</label>
                  <textarea rows={3} value={r.description} data-testid={`firm-ref-desc-${i}`} onChange={(e) => updRef(i, "description", e.target.value)} placeholder="napr. RD Trnava, EPS 140 mm" className="w-full bg-transparent border border-white/15 px-2 py-1.5 text-sm resize-none focus:border-gold outline-none" />
                </div>
                <button onClick={() => delRef(i)} className="md:col-span-1 text-red-400 flex md:justify-center pt-5" data-testid={`firm-ref-delete-${i}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </section>

        <div className="flex flex-wrap gap-3">
          <button onClick={() => persist(false)} disabled={saving} data-testid="firm-save" className="border border-white/25 text-white px-6 py-3 font-bold uppercase tracking-wider hover:border-gold/60 flex items-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Uložiť rozpracované
          </button>
          <button onClick={() => persist(true)} disabled={saving} data-testid="firm-submit-card" className="bg-gold text-ink px-6 py-3 font-bold uppercase tracking-wider hover:bg-gold-light flex items-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Odoslať na schválenie
          </button>
        </div>
      </div>
    </div>
  );
}
